import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GoogleMap, LoadScriptNext, Marker } from '@react-google-maps/api';
import config from '../config';

const ScanLocationsMap = ({ scansList, handleScanClickInMap, selectedMarkerIdFromParent, disabledMessage }) => {

    const defaultCenter = { lat: 64.0, lng: 15.0 };
    const defaultZoom = 4;

    const [selectedMarkerId, setSelectedMarkerId]	= useState(null);
    const [isLoaded, setIsLoaded]					= useState(false);
    const [center, setCenter] = useState(defaultCenter);
    const [zoom, setZoom] = useState(defaultZoom);
    const mapRef = useRef(null);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    useEffect(() => {
        // Sync selected marker with parent component's selectedMarkerId
        setSelectedMarkerId(selectedMarkerIdFromParent);

        // Pan and zoom to the selected marker
        if (selectedMarkerIdFromParent != null && scansList.length > 0) {
            const selectedScan = scansList.find(s => s.id === selectedMarkerIdFromParent);
            if (selectedScan) {
                setCenter({ lat: selectedScan.x, lng: selectedScan.y });
                setZoom(12);
            }
        }
    }, [selectedMarkerIdFromParent, scansList]);

    const containerStyle = {
        width: '100%',
        height: '400px',
        position: 'relative' // Ensure relative positioning for overlay
    };

    const overlayStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent white background
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1, // Ensure overlay is above the map
        fontSize: '20px',
        fontWeight: 'bold'
    };

    const defaultIcon = "http://maps.google.com/mapfiles/ms/icons/red-dot.png"; // Default icon
    const selectedIcon = "http://maps.google.com/mapfiles/ms/icons/green-dot.png"; // Icon for clicked marker

    // Offset overlapping markers so they fan out and are all clickable.
    // Groups markers by identical lat/lng, then spreads each group in a circle.
    const offsetScans = React.useMemo(() => {
        const groups = {};
        scansList.forEach(scan => {
            const key = `${scan.x},${scan.y}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(scan);
        });

        const result = [];
        const OFFSET = 0.0003; // ~30 meters at mid-latitudes
        Object.values(groups).forEach(group => {
            if (group.length === 1) {
                result.push({ ...group[0], displayLat: group[0].x, displayLng: group[0].y });
            } else {
                group.forEach((scan, i) => {
                    const angle = (2 * Math.PI * i) / group.length;
                    result.push({
                        ...scan,
                        displayLat: scan.x + OFFSET * Math.cos(angle),
                        displayLng: scan.y + OFFSET * Math.sin(angle),
                    });
                });
            }
        });
        return result;
    }, [scansList]);

    const handleMarkerClick = (scanId) => {
        setSelectedMarkerId(scanId); // Update state with the clicked marker ID
        handleScanClickInMap(scanId); // Call the parent click handler
    };

    return (
        <div style={{ position: 'relative' }}>
            {disabledMessage && (
                <div style={overlayStyle}>
					{disabledMessage}
                </div>
            )}
            <LoadScriptNext googleMapsApiKey={config.googleMapsKey}>
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={center}
                    zoom={zoom}
                    options={{ disableDefaultUI: disabledMessage }} // Disable default UI if no scans
                    onLoad={onMapLoad}
                >
                    {offsetScans.map((scan) => (
                        <Marker
                            key={scan.id}
                            position={{ lat: scan.displayLat, lng: scan.displayLng }}
                            icon={scan.id === selectedMarkerId ? selectedIcon : defaultIcon}
                            onClick={() => handleMarkerClick(scan.id)}
                        />
                    ))}
                </GoogleMap>
            </LoadScriptNext>
        </div>
    );
};

export default ScanLocationsMap;

